import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Checkbox,
  DataTableSkeleton,
  InlineNotification,
  OverflowMenu,
  Search,
} from '@carbon/react';
import {
  Close,
  Column,
  OpenPanelFilledLeft,
  ResetAlt,
} from '@carbon/react/icons';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { useDebouncedChange } from 'components/debounced-input';
import { Pagination } from 'components/pagination';
import { WELL_STATUS_LABELS, type WellStatusToken } from 'types/well-workspace';
import type {
  WellboreMapData,
  WellWithWellboresResult,
} from 'types/api-responses';
import {
  CATALOG_PAGE_SIZE,
  fetchReconcileWellDetails,
  useWellCatalogBrowse,
} from './hooks';
import { AddWellsFilters } from './add-wells-filters';
import {
  buildCandidateFromDetail,
  buildGeometryFromDetails,
} from './pool-utils';
import { ADD_WELLS_COLUMNS, type AddWellsColumnKey } from './add-wells-utils';
import {
  AddWellsResultsTable,
  type AddWellsRow,
} from './add-wells-results-table';
import type { WellCandidate } from './consolidation-utils';
import styles from './add-wells-tearsheet.module.scss';

/** Matches the reconcile sheet's height so it sits under the same 3rem top gap. */
const SHEET_HEIGHT = 'calc(100% - 3rem)';

/** Above the base reconcile drawer (react-modern-drawer defaults to 100) so this overlays it. */
const STACK_Z_INDEX = 1000;

export interface AddWellsToReconcileTearsheetProps {
  open: boolean;
  onClose: () => void;
  /** The viewed well, for the header context line. */
  anchorName: string;
  anchorUwi: string;
  /** Row ids already in the pool, shown as "Added" and not addable again. */
  existingRowIds: Set<string>;
  /** Hands the chosen catalog wells — and their map geometry — to the reconcile sheet's pool. */
  onAdd: (
    candidates: WellCandidate[],
    geometry: Record<string, WellboreMapData>
  ) => void;
}

const PAGE_SIZES = [25, 50, CATALOG_PAGE_SIZE];

/**
 * The wells picked so far, kept whole by uwi so a pick survives paging or filtering away from its row.
 * @param {WellWithWellboresResult[]} results The wells on the current page, where a pick can be made.
 * @return {object} The picks, their uwis, and the handlers to change them.
 */
const usePickedWells = (results: WellWithWellboresResult[]) => {
  const [picked, setPicked] = useState<Map<string, WellWithWellboresResult>>(
    new Map()
  );
  const selected = useMemo(() => new Set(picked.keys()), [picked]);

  /**
   * Add or remove one well from the picks.
   * @param {string} uwi The well to toggle.
   */
  const toggle = (uwi: string) =>
    setPicked((prev) => {
      const next = new Map(prev);
      const detail = results.find((result) => result.uwi === uwi);
      if (next.has(uwi)) next.delete(uwi);
      else if (detail) next.set(uwi, detail);
      return next;
    });

  /**
   * Pick, or un-pick, every given well on this page.
   * @param {boolean} checked Whether to pick them.
   * @param {string[]} uwis The addable wells on this page.
   */
  const toggleAll = (checked: boolean, uwis: string[]) =>
    setPicked((prev) => {
      const next = new Map(prev);
      for (const uwi of uwis) {
        const detail = results.find((result) => result.uwi === uwi);
        if (checked && detail) next.set(uwi, detail);
        else next.delete(uwi);
      }
      return next;
    });

  /** Drop every pick. */
  const clearPicked = () => setPicked(new Map());

  return { picked, selected, toggle, toggleAll, clearPicked };
};

/**
 * Adds picked wells to the reconcile. The browse pages carry no geometry, so it is fetched here for
 * the picks only.
 * @return {object} Whether an add is running or failed, and the add itself.
 */
const useAddPicked = () => {
  const [adding, setAdding] = useState(false);
  const [addFailed, setAddFailed] = useState(false);

  /**
   * Fetch the wells, build candidates from them, and hand them up.
   * @param {string[]} uwis The picked wells.
   * @param {Function} onAdd Receives the candidates and their geometry.
   * @param {Function} onDone Runs once the wells are handed up.
   */
  const addPicked = async (
    uwis: string[],
    onAdd: AddWellsToReconcileTearsheetProps['onAdd'],
    onDone: () => void
  ) => {
    setAdding(true);
    setAddFailed(false);
    try {
      const details = await fetchReconcileWellDetails(uwis);
      if (details.length > 0) {
        onAdd(
          details.map(buildCandidateFromDetail),
          buildGeometryFromDetails(details)
        );
      }
      onDone();
    } catch {
      setAddFailed(true);
    } finally {
      setAdding(false);
    }
  };

  return { adding, addFailed, addPicked };
};

interface AddWellsResultsProps {
  error: unknown;
  isLoading: boolean;
  rows: AddWellsRow[];
  selected: Set<string>;
  allSelected: boolean;
  addableUwis: string[];
  hiddenColumns: ReadonlySet<AddWellsColumnKey>;
  onToggle: (uwi: string) => void;
  onToggleAll: (checked: boolean, uwis: string[]) => void;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (next: { page: number; pageSize: number }) => void;
}

/**
 * The results pane: the catalog table and its pagination, or the load-error / loading / empty states.
 * @param {AddWellsResultsProps} props The rows, the selection, and the paging state.
 * @return {JSX.Element} The results pane.
 */
function AddWellsResults({
  error,
  isLoading,
  rows,
  selected,
  allSelected,
  addableUwis,
  hiddenColumns,
  onToggle,
  onToggleAll,
  page,
  pageSize,
  totalItems,
  onPageChange,
}: AddWellsResultsProps) {
  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Could not load the well catalog"
        subtitle="Try again in a moment."
      />
    );
  }
  if (isLoading && rows.length === 0) {
    return (
      <DataTableSkeleton
        showHeader={false}
        showToolbar={false}
        columnCount={5}
        rowCount={6}
      />
    );
  }
  if (rows.length === 0) {
    return <p className={styles.hint}>No wells match those filters.</p>;
  }
  return (
    <>
      <AddWellsResultsTable
        rows={rows}
        selected={selected}
        allSelected={allSelected}
        anyAddable={addableUwis.length > 0}
        hiddenColumns={hiddenColumns}
        onToggle={onToggle}
        onToggleAll={(checked) => onToggleAll(checked, addableUwis)}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZES}
        totalItems={totalItems}
        onChange={onPageChange}
      />
    </>
  );
}

/**
 * The sheet's title block and close button.
 * @param {object} props The viewed well, for the context line, and the close handler.
 * @param {string} props.anchorName The viewed well's name.
 * @param {string} props.anchorUwi The viewed well's UWI.
 * @param {Function} props.onClose Closes the sheet.
 * @return {JSX.Element} The header.
 */
function AddWellsHeader({
  anchorName,
  anchorUwi,
  onClose,
}: {
  anchorName: string;
  anchorUwi: string;
  onClose: () => void;
}) {
  return (
    <DrawerHeader className={styles.header}>
      <div className={styles.heading}>
        <p className={styles.eyebrow}>
          Reconcile Wells · {anchorName} · UWI {anchorUwi}
        </p>
        <h3 className={styles.title}>Add wells to reconcile</h3>
        <p className={styles.subtitle}>
          Bring in any well from the catalog — you aren&apos;t limited to the
          suggested duplicates. Added wells appear in Current Suggestions, ready
          to check into the reconcile.
        </p>
      </div>
      <Button
        kind="ghost"
        size="md"
        hasIconOnly
        iconDescription="Close"
        tooltipPosition="left"
        renderIcon={(props) => <Close size={20} {...props} />}
        onClick={onClose}
      />
    </DrawerHeader>
  );
}

interface AddWellsToolbarProps {
  filtersHidden: boolean;
  onToggleFilters: () => void;
  typed: string;
  onType: (value: string) => void;
  onResetPage: () => void;
  hiddenColumns: ReadonlySet<AddWellsColumnKey>;
  onToggleColumn: (key: AddWellsColumnKey, visible: boolean) => void;
  onShowAllColumns: () => void;
  onHideAllColumns: () => void;
  onResetTable: () => void;
}

/**
 * The table toolbar: Hide/Show Filters, the catalog search, the Show/Hide Columns menu and Reset
 * Table — one row above the results, matching the mock.
 * @param {AddWellsToolbarProps} props The filters-hidden state, the search value, the column
 *   visibility and the handlers.
 * @return {JSX.Element} The toolbar row.
 */
function AddWellsToolbar({
  filtersHidden,
  onToggleFilters,
  typed,
  onType,
  onResetPage,
  hiddenColumns,
  onToggleColumn,
  onShowAllColumns,
  onHideAllColumns,
  onResetTable,
}: AddWellsToolbarProps) {
  return (
    <div className={styles.toolbar}>
      <Button
        kind="ghost"
        renderIcon={OpenPanelFilledLeft}
        onClick={onToggleFilters}
      >
        {filtersHidden ? 'Show Filters' : 'Hide Filters'}
      </Button>
      <Search
        size="lg"
        labelText="Search by well name, UWI, or operator"
        placeholder="Search by well name, UWI, or operator"
        value={typed}
        onChange={(event) => {
          onResetPage();
          onType(event.target.value);
        }}
        onClear={() => {
          onResetPage();
          onType('');
        }}
        className={styles.search}
      />
      <div className={styles.toolbarActions}>
        <OverflowMenu
          renderIcon={Column}
          size="lg"
          flipped
          iconDescription="Show/Hide Columns"
        >
          {/* Wrapped in a fragment so Carbon's cloned closeMenu/focus props land here, not on a real
              DOM node (matching the TableActions pattern). */}
          <>
            <div className={styles.columnsMenuBody}>
              <div className={styles.columnsMenuActions}>
                <button
                  type="button"
                  className={styles.columnsMenuLink}
                  onClick={onShowAllColumns}
                >
                  Show All
                </button>
                <button
                  type="button"
                  className={styles.columnsMenuLink}
                  onClick={onHideAllColumns}
                >
                  Hide All
                </button>
              </div>
              {ADD_WELLS_COLUMNS.map((column) => (
                <Checkbox
                  key={column.key}
                  id={`add-wells-column-${column.key}`}
                  className={styles.columnsMenuItem}
                  labelText={column.label}
                  checked={!hiddenColumns.has(column.key)}
                  onChange={(
                    _event: unknown,
                    { checked }: { checked: boolean }
                  ) => onToggleColumn(column.key, checked)}
                />
              ))}
            </div>
          </>
        </OverflowMenu>
        <Button
          kind="ghost"
          hasIconOnly
          renderIcon={ResetAlt}
          iconDescription="Reset Table"
          tooltipPosition="bottom"
          tooltipAlignment="end"
          onClick={onResetTable}
        />
      </div>
    </div>
  );
}

/**
 * Add wells to reconcile — a faceted, paginated browse of the whole well catalog, stacked over the
 * Reconcile Wells sheet. The operator is not limited to the detector's suggestions: picked wells are
 * appended to Current Suggestions (unchecked), ready to check into the reconcile by hand.
 * @param {AddWellsToReconcileTearsheetProps} props Open state, the anchor, what's pooled, and the add handler.
 * @return {JSX.Element | null} The tearsheet.
 */
export function AddWellsToReconcileTearsheet({
  open,
  onClose,
  anchorName,
  anchorUwi,
  existingRowIds,
  onAdd,
}: AddWellsToReconcileTearsheetProps) {
  // `typed` echoes each keystroke; `term` is the debounced value the query runs on.
  const [term, setTerm] = useState('');
  const [typed, onType] = useDebouncedChange<string>('', setTerm, 400);
  const [statusIds, setStatusIds] = useState<string[]>([]);
  const [operatorIds, setOperatorIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CATALOG_PAGE_SIZE);
  const [filtersHidden, setFiltersHidden] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<AddWellsColumnKey>>(
    new Set()
  );
  const {
    results,
    totalCount,
    statusBuckets,
    operatorBuckets,
    isLoading,
    error,
  } = useWellCatalogBrowse(
    { text: term, statusIds, operatorIds, page, pageSize },
    open
  );

  const rows = useMemo<AddWellsRow[]>(
    () =>
      results.map((detail) => {
        const status = detail.status as WellStatusToken | undefined;
        return {
          uwi: detail.uwi,
          wellName: detail.name ?? detail.uwi,
          operator: detail.currentOperator ?? 'Unknown operator',
          status: status ? (WELL_STATUS_LABELS[status] ?? status) : '—',
          alreadyAdded: existingRowIds.has(detail.uwi),
        };
      }),
    [results, existingRowIds]
  );

  const { picked, selected, toggle, toggleAll, clearPicked } =
    usePickedWells(results);
  const { adding, addFailed, addPicked } = useAddPicked();
  const addable = rows.filter((row) => !row.alreadyAdded);
  const allSelected =
    addable.length > 0 && addable.every((row) => selected.has(row.uwi));

  /** Back to page 1 — a new search or facet selection reshuffles the result set. */
  const resetPage = () => setPage(1);

  /** Clear the facet selections (not the search term) — the mock's "Reset Filters". */
  const resetFilters = () => {
    setStatusIds([]);
    setOperatorIds([]);
    resetPage();
  };

  /**
   * Show or hide one column from the Show/Hide Columns menu.
   * @param {AddWellsColumnKey} key The column to change.
   * @param {boolean} visible Whether it should be shown.
   */
  const toggleColumn = (key: AddWellsColumnKey, visible: boolean) =>
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Show every column — also the Reset Table action. */
  const showAllColumns = () => setHiddenColumns(new Set());

  /** Hide every data column, leaving only the select checkbox. */
  const hideAllColumns = () =>
    setHiddenColumns(new Set(ADD_WELLS_COLUMNS.map((column) => column.key)));

  /** Clear the selection, filters, search term and column visibility — the sheet opens fresh each time. */
  const reset = () => {
    clearPicked();
    setStatusIds([]);
    setOperatorIds([]);
    setPage(1);
    onType('');
    setHiddenColumns(new Set());
  };

  /** Add the picks, then clear the sheet and close it. */
  const handleAdd = () =>
    addPicked([...picked.keys()], onAdd, () => {
      reset();
      onClose();
    });

  /** Close without adding, resetting the sheet for next time. */
  const handleClose = () => {
    reset();
    onClose();
  };

  if (!open) return null;

  // Portaled to <body>, not left inside the reconcile Drawer's subtree: react-modern-drawer transforms
  // its panel, and a transformed ancestor becomes the containing block for this fixed-position sheet —
  // which would size and offset it against the inset reconcile sheet instead of the viewport. Portaling
  // one layer up lets it fill the same rectangle as the reconcile sheet.
  return createPortal(
    <Drawer
      lockBackgroundScroll
      open={open}
      direction="bottom"
      size={SHEET_HEIGHT}
      zIndex={STACK_Z_INDEX}
      onClose={handleClose}
      className={styles.tearsheet}
    >
      <AddWellsHeader
        anchorName={anchorName}
        anchorUwi={anchorUwi}
        onClose={handleClose}
      />

      <DrawerContent className={styles.body}>
        <div className={styles.workspace}>
          {filtersHidden ? null : (
            <AddWellsFilters
              statusBuckets={statusBuckets}
              operatorBuckets={operatorBuckets}
              statusIds={statusIds}
              operatorIds={operatorIds}
              onChangeStatus={(ids) => {
                resetPage();
                setStatusIds(ids);
              }}
              onChangeOperator={(ids) => {
                resetPage();
                setOperatorIds(ids);
              }}
              onReset={resetFilters}
            />
          )}
          <div className={styles.tableArea}>
            <AddWellsToolbar
              filtersHidden={filtersHidden}
              onToggleFilters={() => setFiltersHidden((hidden) => !hidden)}
              typed={typed}
              onType={onType}
              onResetPage={resetPage}
              hiddenColumns={hiddenColumns}
              onToggleColumn={toggleColumn}
              onShowAllColumns={showAllColumns}
              onHideAllColumns={hideAllColumns}
              onResetTable={showAllColumns}
            />
            <AddWellsResults
              error={error}
              isLoading={isLoading}
              rows={rows}
              selected={selected}
              allSelected={allSelected}
              addableUwis={addable.map((row) => row.uwi)}
              hiddenColumns={hiddenColumns}
              onToggle={toggle}
              onToggleAll={toggleAll}
              page={page}
              pageSize={pageSize}
              totalItems={totalCount}
              onPageChange={({ page: nextPage, pageSize: nextSize }) => {
                setPage(nextSize === pageSize ? nextPage : 1);
                setPageSize(nextSize);
              }}
            />
          </div>
        </div>
      </DrawerContent>

      {addFailed ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not add the selected wells"
          subtitle="Try again in a moment."
        />
      ) : null}

      <div className={styles.footer}>
        <Button kind="secondary" onClick={handleClose}>
          Back to reconcile
        </Button>
        <Button
          kind="primary"
          disabled={picked.size === 0 || adding}
          onClick={handleAdd}
        >
          {picked.size > 1 ? `Add ${picked.size} wells` : 'Add well'}
        </Button>
      </div>
    </Drawer>,
    document.body
  );
}
